<template>
  <ul :class="['c-pagination-navigation', { 'c-pagination-navigation--is-compact': isCompact }]">
    <li class="c-pagination-navigation__pagination-item">
      <base-button variant="link" class="c-pagination-navigation__pagination-link" @click="onClickFirstPage" :disabled="isInFirstPage" aria-label="Go to first page">
        <font-awesome-icon :icon="['fal', 'chevron-double-left']"/>
      </base-button>
    </li>

    <li class="c-pagination-navigation__pagination-item">
      <base-button variant="link" class="c-pagination-navigation__pagination-link" @click="onClickPreviousPage" :disabled="isInFirstPage" aria-label="Go to previous page">
        <font-awesome-icon :icon="['fal', 'chevron-left']"/>
      </base-button>
    </li>

    <li class="c-pagination-navigation__pagination-item" v-if="startPage > 1">
      <base-button variant="link" class="c-pagination-navigation__pagination-link" :disabled="true" role="separator">
        {{ '\u2026' }}
      </base-button>
    </li>

    <li :key="index" v-for="(page, index) in pages" class="c-pagination-navigation__pagination-item">
      <base-button variant="link" class="c-pagination-navigation__pagination-link" :class="{ active: isPageActive(page.number) }" @click="onClickPage(page.number)" :disabled="page.isDisabled" :aria-label="`Go to page number ${page.number}`">
        {{ page.number }}
      </base-button>
    </li>

    <li class="c-pagination-navigation__pagination-item" v-if="endPage < totalPages">
      <base-button variant="link" class="c-pagination-navigation__pagination-link" :disabled="true" role="separator">
        {{ '\u2026' }}
      </base-button>
    </li>

    <li class="c-pagination-navigation__pagination-item">
      <base-button variant="link" class="c-pagination-navigation__pagination-link" @click="onClickNextPage" :disabled="isInLastPage" aria-label="Go to next page" >
        <font-awesome-icon :icon="['fal', 'chevron-right']"/>
      </base-button>
    </li>

    <li class="c-pagination-navigation__pagination-item">
      <base-button variant="link" class="c-pagination-navigation__pagination-link" @click="onClickLastPage" :disabled="isInLastPage" aria-label="Go to last page" >
        <font-awesome-icon :icon="['fal', 'chevron-double-right']"/>
      </base-button>
    </li>
  </ul>
</template>

<script>
export default {
  name: 'Pagination',

  props: {
    visibleButtons: {
      type: Number,
      default: 3
    },
    totalCount: {
      type: Number,
      required: true
    },
    perPage: {
      type: Number,
      required: true
    },
    currentPage: {
      type: Number,
      required: true
    },
    isCompact: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    /**
     * Calculate the total pages count
     *
     * @return {number}
     */
    totalPages () {
      return Math.ceil(this.totalCount / this.perPage)
    },

    /**
     * Calculate the starting page number.
     *
     * @return {number}
     */
    startPage () {
      if (this.currentPage < 3) {
        return 1
      }

      if (this.currentPage === this.totalPages) {
        return this.totalPages - this.visibleButtons + 1
      }

      return this.currentPage - 1
    },

    /**
     * Calculate the ending page number
     *
     * @return {number}
     */
    endPage () {
      return Math.min(this.startPage + this.visibleButtons - 1, this.totalPages)
    },

    /**
     * Generate a list of pages
     *
     * @returns {Array}
     */
    pages () {
      return Array.from({ length: this.totalPages <= this.visibleButtons ? this.totalPages : this.visibleButtons }, (value, index) => this.startPage + index).map(item => ({ number: item, isDisabled: item === this.currentPage }))
    },

    /**
     * Is the first page, also the current page
     *
     * @returns {Boolean}
     */
    isInFirstPage () {
      return this.currentPage === 1
    },

    /**
     * Is the last page, also the current page
     *
     * @returns {Boolean}
     */
    isInLastPage () {
      return this.currentPage === this.totalPages
    }
  },

  methods: {
    /**
     * Event handler for "Go to first page"
     */
    onClickFirstPage () {
      this.$emit('change', 1)
    },

    /**
     * Event handler for "Go to previous page"
     */
    onClickPreviousPage () {
      this.$emit('change', this.currentPage - 1)
    },

    /**
     * Event handler for "Go to page number {page}"
     */
    onClickPage (page) {
      this.$emit('change', page)
    },

    /**
     * Event handler for "Go to next page"
     */
    onClickNextPage () {
      this.$emit('change', this.currentPage + 1)
    },

    /**
     * Event handler for "Go to last page"
     */
    onClickLastPage () {
      this.$emit('change', this.totalPages)
    },

    /**
     * Is the page number the same as the current page
     */
    isPageActive (page) {
      return this.currentPage === page
    }
  }
}
</script>
